import { useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { Button } from '@/components/ui/Button';
import { Checkbox, Radio } from '@/components/ui/Choice';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { TextField } from '@/components/ui/TextField';
import { paths } from '@/routes/paths';
import type { ApiError } from '@/services/http/api-error';
import { Portal } from '@/types/auth';
import { useLogin } from '../hooks/useLogin';

const PORTALS: { value: Portal; label: string }[] = [
  { value: Portal.PLATFORM, label: 'Super Admin' },
  { value: Portal.CLIENT, label: 'Client Admin' },
];

interface FieldErrors {
  identifier?: string;
  password?: string;
}

function validate(identifier: string, password: string): FieldErrors {
  const errors: FieldErrors = {};
  if (!identifier.trim()) errors.identifier = 'Enter your mobile number or email.';
  if (!password) errors.password = 'Enter your password.';
  return errors;
}

/** Branch on the error code, never the server message. */
function loginErrorMessage(error: ApiError): string {
  switch (error.code) {
    case 'UNAUTHORIZED':
    case 'VALIDATION_FAILED':
      return 'Incorrect mobile number/email or password.';
    case 'FORBIDDEN':
      return 'This account cannot sign in here. Check the login type, or contact your administrator.';
    case 'ACCOUNT_LOCKED':
      return 'Too many failed attempts; your account is temporarily locked. Try again later.';
    case 'ACCOUNT_INACTIVE':
      return 'This account is inactive. Contact your administrator.';
    case 'RATE_LIMITED':
      return 'Too many sign-in attempts. Please wait a minute and try again.';
    case 'NETWORK_ERROR':
    case 'TIMEOUT':
      return 'Unable to reach the server. Check your connection and try again.';
    default:
      return 'Sign-in failed. Please try again.';
  }
}

export function LoginForm() {
  const login = useLogin();
  const [portal, setPortal] = useState<Portal>(Portal.PLATFORM);
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(false);
  const [errors, setErrors] = useState<FieldErrors>({});

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const found = validate(identifier, password);
    setErrors(found);
    if (found.identifier || found.password) return;
    login.mutate({ portal, identifier: identifier.trim(), password, remember });
  };

  return (
    <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
      <fieldset className="flex flex-wrap gap-x-6 gap-y-2">
        <legend className="sr-only">Login type</legend>
        {PORTALS.map((p) => (
          <Radio
            key={p.value}
            name="portal"
            value={p.value}
            label={p.label}
            checked={portal === p.value}
            onChange={() => setPortal(p.value)}
          />
        ))}
      </fieldset>

      <TextField
        label="Mobile Number / Email"
        name="identifier"
        autoComplete="username"
        placeholder="Enter mobile number or email"
        value={identifier}
        onChange={(e) => setIdentifier(e.target.value)}
        error={errors.identifier}
      />
      <TextField
        label="Password"
        name="password"
        type="password"
        autoComplete="current-password"
        placeholder="Enter password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        error={errors.password}
      />

      <div className="flex items-center justify-between gap-3">
        <Checkbox
          label="Remember me"
          checked={remember}
          onChange={(e) => setRemember(e.target.checked)}
        />
        <Link to={paths.forgotPassword} className="text-xs text-brand hover:underline">
          Forgot Password ?
        </Link>
      </div>

      {login.error && (
        <ErrorAlert message={loginErrorMessage(login.error)} requestId={login.error.requestId} />
      )}

      <Button type="submit" loading={login.isPending} className="w-full">
        Sign In
      </Button>
    </form>
  );
}
